import { useRef, useEffect, useState } from 'react';
import { Redirect } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import cx from 'classnames';

import { ROUTE, TEXT } from 'common/const';
import { track } from 'common/utils';
import { Content, TextLink } from 'common/components';
import { Page } from 'common/site';
import { register } from 'data/customers';

import SignupAddress from './SignupAddress';
import SignupCredentials from './SignupCredentials';

import styles from './Signup.module.css';
import ReCAPTCHA from 'react-google-recaptcha';

const STEP = {
  CREDENTIALS: 'credentials',
  ADDRESS: 'address',
  RECAPTCHA: 'recaptcha',
  SUBMIT: 'submit',
};

const Signup = () => {
  const [recaptchaValidated, setRecaptchaValidated] = useState(true);
  const recaptcha = useRef();
  const [credentials, setCredentials] = useState({});
  const [address, setAddress] = useState({});
  const [step, setStep] = useState(STEP.CREDENTIALS);

  const {
    mutate,
    isLoading: isSubmitting,
    error,
    isSuccess,
  } = useMutation(register, {
    onSuccess: () => track('sign_up'),
  });

  useEffect(() => {
    if (step === STEP.SUBMIT) {
      mutate({
        ...credentials,
        ...address,
      });
    }
  }, [address, credentials, mutate, step]);

  return isSuccess ? (
    <Redirect to={ROUTE.LOGIN} />
  ) : (
    <Page
      variant="island"
      title={
        step === STEP.ADDRESS
          ? 'Enter Your Account Details'
          : 'Create an Account'
      }
    >
      <Content isLoading={isSubmitting} error={error?.message}>
        {/* If email is invalid show credentials page */}
        {step === STEP.CREDENTIALS && (
          // First page with the email
          <SignupCredentials
            onSubmit={(signupData) => {
              setCredentials(signupData);
              setStep(STEP.ADDRESS);
            }}
          />
        )}

        {step === STEP.ADDRESS && (
          // If login is not complete show account details page
          <SignupAddress
            onSubmit={(addressData) => {
              setAddress(addressData);
              setStep(STEP.RECAPTCHA);
            }}
          />
        )}

        {step === STEP.RECAPTCHA && (
          <>
            <ReCAPTCHA
              className={styles.recaptcha}
              ref={recaptcha}
              sitekey={process.env.REACT_APP_SITE_KEY}
              onChange={() => {
                // You can handle onChange to immediately check if reCAPTCHA was solved
                if (recaptcha.current && recaptcha.current.getValue()) {
                  setRecaptchaValidated(true);
                  setStep(STEP.SUBMIT); // Only proceed to SUBMIT if reCAPTCHA is solved
                }
              }}
            />
            {!recaptchaValidated && (
              <div className={styles.errorMessage}>
                Please verify that you are not a robot.
              </div>
            )}
          </>
        )}

        <p className={cx(TEXT.BODY_1, styles.footer)}>
          Already have an account? <TextLink to={ROUTE.LOGIN}>Login</TextLink>
        </p>
      </Content>
    </Page>
  );
};

export default Signup;
